import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useMutation } from 'react-apollo';
import Cookies from 'js-cookie';
import { T, t } from '@sm/intl';
import {
  OpenEndedAnswerType,
  RespondentCapabilityProvider,
  ExternalQuestion,
} from '@sm/question-widgets/respondent-survey';
import classNames from 'classnames';
import { SubmitSurveyResponseMutation, SubmitSurveyResponseMutationVariables } from '~lib/generatedGqlTypes';
import submitSurveyResponse from './SubmitResponses.graphql';
import { useVerifySurveyVersion } from './hooks/useVerifySurveyVersion';
import { BypassBlock } from '~app/components/core/BypassBlock';
import { ExitLink } from '~app/components/Survey/ExitLink';
import { SurveyTitle } from '~app/components/Survey/SurveyTitleV2';
import createHeadingLevel from './helper/createHeadingLevel';
import SurveyFooter from '~app/components/Survey/FooterContainer';
import { PageTitle } from '~app/components/Survey/PageTitle';
import { PageDescription } from '~app/components/Survey/PageDescription';
import { SurveyButton } from '~app/components/Survey/SurveyButton';
import { Layout } from './components/Layout';
import { QuestionList } from './components/QuestionList';
import { PersistentProgressBar } from '~app/components/Survey/PersistentProgressBar';
import ModifiedSurveyDialog from '~app/components/Survey/ModifiedSurveyDialog';
import extrapolateSurveyData from './helper/extrapolateSurveyData';
import useStyles from './useStyles';
import { Logo } from './components/Logo';
import useCenterContainerOffset from './hooks/useCenterContainerOffset';
import { PageNavigation } from './components/PageNavigation';
import { ClassicProgressBar } from '~app/components/Survey/ClassicProgressBar';
import { ActiveViewScrollManager, SurveyFormatView, selectViews } from '~app/components/Survey/SurveyFormat';
import SurveyFormat from './helper/surveyFormatEnum';
import { runAfterFocusChangedFn, useGlobalAfterFocusChangedHandler } from './validation/delayedValidation';
import { useLayoutEffect, useAppSelector, useAppDispatch } from '~app/hooks';
import { decodeHtmlEntities } from './helper/decodeHtmlEntities';
import { selectHasVersionChanged, setErrors, focusFirstQuestionError, validatePage } from './errors/errorsSlice';
import {
  selectSurvey,
  selectCurrentPage,
  selectCollector,
  selectQuestions,
  updateAnswer,
  submitSurvey,
  selectClientToken,
} from './surveySlice';

const COPY = {
  SKIP_LINK_CONTENT: {
    id: 'SurveyTaking.SkipLinkContent',
    defaultMessage: 'Skip to content',
    description: '[Type: label][Vis: med] - Skip to content link label (Accessibility)',
  },
  OK_LABEL: {
    id: 'SurveyTaking.OK_LABEL',
    defaultMessage: 'OK',
    description: '[Type: button][Vis: high] - question/description ok label for One-Question-At-A-Time mode',
  },
};

const Survey: React.FC = () => {
  const survey = useAppSelector(selectSurvey);
  const collector = useAppSelector(selectCollector);
  const activePage = useAppSelector(selectCurrentPage);
  const clientToken = useAppSelector(selectClientToken);
  const views = useAppSelector(selectViews);
  const questions = useAppSelector(selectQuestions);
  const hasVersionChanged = useAppSelector(selectHasVersionChanged);
  const dispatch = useAppDispatch();

  const handlePageReload = useCallback(() => window.location.reload(), []);

  /** The questions to be used by QuestionList */
  const mappedQuestions = useMemo(
    () =>
      questions.map(
        q =>
          ({
            ...q,
            questionBank: null,
            errors: [],
            disabled: false,
            questionNumber: q.position.inSurvey,
            title: q.heading.default.text,
            typeId: 'single_textbox',
            // Something here is broken/incompatible at the moment (rchang)
          } as unknown as ExternalQuestion)
      ),
    [questions]
  );

  const layoutContainerRef = React.useRef<HTMLDivElement>(null);
  const persistentProgressRef = React.useRef<HTMLDivElement>(null);
  const partialContainerRef = React.useRef<HTMLDivElement>(null);
  const logoContainerRef = React.useRef<HTMLDivElement>(null);
  const toolbarContainerRef = React.useRef<HTMLDivElement>(null);
  const topContainerRef = React.useRef<HTMLDivElement>(null);
  const mainContainerRef = React.useRef<HTMLDivElement>(null);
  const pageNavigationRef = React.useRef<HTMLDivElement>(null);
  const surveyFooterRef = React.useRef<HTMLDivElement>(null);
  const mainRef = React.useRef<HTMLDivElement>(null);

  const [firstViewElement, setFirstViewElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setFirstViewElement(document.getElementById(`view-${views[0]}`));
  }, [views]);

  const offsetTop = useCenterContainerOffset(
    firstViewElement,
    mainContainerRef,
    [persistentProgressRef],
    [topContainerRef],
    (margin: number) => {
      if (hasVersionChanged) return margin;
      if (survey?.design?.theme?.version === 'V3') {
        const cr = window.getComputedStyle(mainContainerRef?.current as Element);
        return Math.abs(margin / 2 - parseInt(cr.paddingTop, 10));
      }
      return margin;
    }
  );

  /**
   * calculateOffsetBottom
   * Calculate bottom margin for the SurveyFooter. This ensures there is
   * enough room for footer links and last question can move to centre page.
   */
  const [offsetBottom, setOffsetBottom] = useState(0);
  useLayoutEffect(() => {
    const calculateOffsetBottom = (): void => {
      if (survey?.format !== SurveyFormat.OQAAT) return;
      const footerHeight = surveyFooterRef.current?.offsetHeight ?? 0;
      const progressHeight = persistentProgressRef.current?.offsetHeight ?? 0;
      const offset = footerHeight + progressHeight;
      setOffsetBottom(survey?.design?.footer?.enabled ? window.innerHeight / 2 - offset : offset);
    };
    window.addEventListener('resize', calculateOffsetBottom);
    calculateOffsetBottom(); // run immediately
    return () => window.removeEventListener('resize', calculateOffsetBottom);
  }, [survey?.design?.footer?.enabled, survey?.format, surveyFooterRef, persistentProgressRef, setOffsetBottom]);
  useGlobalAfterFocusChangedHandler();

  const [submitCurrentResponses] = useMutation<SubmitSurveyResponseMutation, SubmitSurveyResponseMutationVariables>(
    submitSurveyResponse,
    {
      context: {
        headers: {
          'x-client-token': clientToken ?? null,
        },
      },
    }
  );

  /** Queries the survey version and applies version-changed errors when called */
  const verifySurveyVersion = useVerifySurveyVersion();

  // Whether the Done button should be disabled
  const [disableSubmit, setDisableSubmit] = useState(false);

  const dateStarted = React.useRef('');

  // Set StartTime on Load
  useEffect(() => {
    dateStarted.current = new Date().toISOString();
  }, []);

  const surveyDone = useCallback(
    async () => {
      runAfterFocusChangedFn();
      // dateCompleted has to be declared before validatePageResult due to an issue
      // with jest timers and promises which was breaking an integration test.
      const dateCompleted = new Date().toISOString();
      const isValid = await dispatch(validatePage()).unwrap();

      if (!isValid) {
        dispatch(focusFirstQuestionError());
        void verifySurveyVersion();
        return;
      }

      // disable the Done button while submitting
      setDisableSubmit(true);

      const response = await dispatch(
        submitSurvey({
          mappedQuestions,
          mutation: submitCurrentResponses,
          dateStarted: dateStarted.current,
          dateCompleted,
        })
      ).unwrap();

      if (!response?.success) {
        dispatch(setErrors(response?.userErrors || []));
        // re-enable Done button if survey submission fails
        setDisableSubmit(false);
        return;
      }

      // _manageSurveyTakingCookies();
      Cookies.set(`RP_${collector?.id}`, response?.encryptedSmParam || '');
      // _redirector();
      // use replace to avoid going back to survey using back button
      window.location.replace(`/survey-thanks/?sm=${response?.encryptedSmParam || ''}`);
    },
    // mappedQuestions are already memoized so ideally they'll never change (as long as questions dont change) but useCallback doesn't have that info. So have to include it in dependency array.
    [dispatch, mappedQuestions, submitCurrentResponses, collector?.id, verifySurveyVersion]
  );

  const {
    surveyTitleContainer,
    mainContainer,
    layoutMainContainer,
    questionContainer,
    pageFooterContainer,
    sronlyFocus,
    pageDescriptionOkButton,
    pageNavigationContainer,
    surveyFooterContainer,
  } = useStyles({ offsetTop, offsetBottom });

  if (!survey || Object.entries(survey).length === 0) {
    return null;
  }

  const {
    isExitLinkEnabled,
    isSurveyTitleEnabled,
    showPageTitles,
    showPageNumbers,
    isFooterEnabled,
    isOQAAT,
    isClassicMode,
    isProgressBarEnabled,
    totalQuestionsCount,
    isQuestionNumberingEnabled,
    showRequiredQuestionAsterisks,
    surveyTitleAlignment,
    labels,
    totalPages,
    logo: { enabled: isLogoEnabled, ...logoProps },
    progressBar: { position: progressBarPosition, showPercent, showPageCount },
  } = extrapolateSurveyData(survey);

  const surveyTitleHTML = survey?.titleHTML;

  const surveyId = survey?.id;
  /**
   * Page Header
   * calculate proper Page Title and Description based on current page
   */
  const hasPageDescription = !!activePage?.subHeading;
  const pageDescription = activePage?.subHeading;
  const pageTitle = activePage?.heading;
  const displayPageTitle = showPageTitles && !!pageTitle;
  /**
   * Skip to content will attempt to move the focus position (without actually focusing)
   * to the Survey Title (if exists), then Page title (if exists), finally to the
   * main-content container (<main>).
   * `tabIndex={-1}` is added to each element (backwards compat with <= IE 11)
   */
  const surveyTitleId = `survey-title-${surveyId}`;
  const pageTitleId = `page-title-${surveyId}`;
  const mainContentId = `main-content-${surveyId}`;
  const skipContentId = mainContentId;

  const currentPage = activePage?.position ?? 1;
  const nextHeadingElement = createHeadingLevel();

  const layoutContainerOptions = {
    layoutContainer: { ref: layoutContainerRef, 'data-survey-format': survey?.format },
    partialContainer: { ref: partialContainerRef },
    topContainer: { ref: topContainerRef },
    mainContainer: { className: layoutMainContainer, ref: mainContainerRef },
    logoContainer: { ref: logoContainerRef },
    toolbarContainer: { ref: toolbarContainerRef },
  };

  const settings = {
    survey: {
      numberingEnabled: isQuestionNumberingEnabled,
      asteriskEnabled: showRequiredQuestionAsterisks,
      okLabel: decodeHtmlEntities(labels.ok) || t(COPY.OK_LABEL),
    },
  };

  const upstreamActions = {
    onLeaveQuestion: (externalQuestion: ExternalQuestion, answer: OpenEndedAnswerType) => {
      if (questions) {
        dispatch(updateAnswer(answer));
      }
    },
  };

  return (
    <>
      <ActiveViewScrollManager />
      <BypassBlock>
        <a href={`#${skipContentId}`}>
          <T desc={COPY.SKIP_LINK_CONTENT} />
        </a>
      </BypassBlock>
      {hasVersionChanged && <ModifiedSurveyDialog onConfirmClick={handlePageReload} />}
      <Layout
        logo={!!isLogoEnabled && <Logo {...logoProps} />}
        tools={!!isExitLinkEnabled && <ExitLink href="#" label={labels.exit} />}
        options={layoutContainerOptions}
      >
        <div id={`survey-title-container-${surveyId}`} className={surveyTitleContainer}>
          {isSurveyTitleEnabled && (
            <SurveyTitle
              id={surveyTitleId}
              align={surveyTitleAlignment}
              element={nextHeadingElement(0)}
              text={surveyTitleHTML || ''}
            />
          )}
          {/* <SurveyIntro text={surveyIntro} /> */}
          {/* <SurveyButton translate="no" size="md">OK</SurveyButton> */}
        </div>
        <main id={mainContentId} ref={mainRef} className={mainContainer} tabIndex={-1}>
          <div id={`page-header-container-${surveyId}`}>
            {displayPageTitle && (
              <PageTitle
                id={pageTitleId}
                element={nextHeadingElement(1)}
                text={pageTitle || ''}
                numbered={showPageNumbers}
                number={currentPage}
              />
            )}
            {hasPageDescription && (
              <SurveyFormatView name="pageDescription" noShield>
                {(isActive, setNextActive) => (
                  <>
                    <PageDescription text={pageDescription || ''} />
                    {isOQAAT && (
                      <SurveyButton
                        size="md"
                        translate="no"
                        onClick={setNextActive}
                        className={classNames(pageDescriptionOkButton, { [sronlyFocus]: !isActive })}
                      >
                        {labels.ok || t(COPY.OK_LABEL)}
                      </SurveyButton>
                    )}
                  </>
                )}
              </SurveyFormatView>
            )}
          </div>

          <RespondentCapabilityProvider settings={settings} upstreamActions={upstreamActions}>
            <form id={`form-${surveyId}`} className={questionContainer}>
              {!!isProgressBarEnabled && isClassicMode && progressBarPosition === 'TOP' && (
                <ClassicProgressBar
                  value={currentPage}
                  total={totalPages ?? 1}
                  showPageCount={showPageCount ?? true}
                  showPercentage={showPercent ?? true}
                />
              )}
              {/**
               * NOTE: active (boolean) will be controlled by Progression Engine
               * NOTE: Are all the nullified fields (questionBank, validation) needed?
               */}
              <QuestionList questions={mappedQuestions} isOQAAT={isOQAAT} />
              {/**
               *  When survey is in OQAAT mode, progress bar is below the survey footer and is sticky
               */}
              {!!isProgressBarEnabled && isOQAAT && (
                <PersistentProgressBar total={totalQuestionsCount} ref={persistentProgressRef} />
              )}
              {!!isProgressBarEnabled && isClassicMode && progressBarPosition === 'BOTTOM' && (
                <ClassicProgressBar
                  value={currentPage}
                  total={totalPages ?? 1}
                  showPageCount={showPageCount ?? true}
                  showPercentage={showPercent ?? true}
                />
              )}
            </form>
          </RespondentCapabilityProvider>
          <footer id={`page-footer-container-${surveyId}`} className={pageFooterContainer}>
            <SurveyFormatView name="pageNavigation" noShield>
              <PageNavigation
                ref={pageNavigationRef}
                className={pageNavigationContainer}
                labels={labels}
                currentPage={currentPage}
                totalPages={totalPages ?? 1}
                onDone={async () => surveyDone()}
                disableSubmit={disableSubmit}
              />
            </SurveyFormatView>
          </footer>
        </main>
        <footer id={`survey-footer-container-${surveyId}`} className={surveyFooterContainer} ref={surveyFooterRef}>
          {isFooterEnabled && (
            <SurveyFooter
              element="div"
              isGrayScale={!!survey?.design?.footer?.grayscale}
              showPrivacyCookiePolicy
              // Below are placeholders, fill in when we get info from query
              showReportProblem={false}
              reportProblemURL=""
              isGoogleSubdomain={false}
              googlePrivacyPolicyURL=""
            />
          )}
        </footer>
      </Layout>
    </>
  );
};

export default Survey;
